import { inject } from '@angular/core';
import { AppFeatureActions } from '@app/app.actions';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { TICKET_ASSIGNMENT_DEBOUNCE_TIME } from '@app/shared/tokens';
import { FileKind } from '@core/data-access/models/file-upload.model';
import { PersonType } from '@core/data-access/models/person.model';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import { QuestionnaireApiService } from '@core/data-access/services/api/questionnaire-api.service';
import { UploadTariffDocumentAPI } from '@core/data-access/services/api/upload-tariff-document-api';
import { BuyerFormsSyncActions, FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { TicketSendingOptionsActions } from '@core/data-access/store/shared/actions/ticket-sending-option.actions';
import { selectSelectedTicketSendingOptionByTariffUuid } from '@core/data-access/store/shared/shared.selectors';
import { SavedTicketHolderActions } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import { selectSavedTicketHolders } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import {
  selectBuyerInformationFormFields,
  selectBuyerPerson
} from '@core/features/buyer-information/store/buyer-information.selectors';
import { selectBuyerQuestionnaireFormFields } from '@core/features/buyer-questionnaire/store/buyer-questionnaire.selectors';
import {
  TicketAssignmentActions,
  TicketHolderActions,
  TicketHolderDocumentActions
} from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import {
  selectTicketAssignmentTicketHolderByPersonUuid,
  selectTicketHolderBuyerForms,
  selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId,
  selectTicketHolderQuestionnaireForms
} from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import {
  getTicketHolderQuestionnairePipe,
  syncCVDocumentFormFields,
  syncGDPRConsentAndCVDocumentFormFields
} from '@core/features/ticket-assignment/utils/ticket-assignment-effects-helpers';
import { mapUploadTariffDocumentResponseToUploadedFiles } from '@core/features/ticket-assignment/utils/ticket-holder-uploaded-documents-mappers';
import { setSyncedQuestionnaireFormFieldsVisibility } from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import { setDisabledFormFields } from '@core/utils/form-fields/form-field-helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { BookingActions } from '@store/products/booking/booking.actions';
import { selectBookedTariffUuids } from '@store/products/booking/booking.selectors';
import { selectTicketPersonIdByBookedTariffUuid } from '@store/products/booking/tariff/booking-tariff.selectors';
import { map as _map, isEmpty, isNil, isNull, merge } from 'lodash';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  first,
  forkJoin,
  iif,
  map,
  mergeMap,
  of,
  switchMap,
  withLatestFrom
} from 'rxjs';

export const setTicketHolderFormsAndDynamicQuestionnaireFieldsOnAddedTicketAssignmentFeature = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    ticketAssignmentDebounceTime = inject(TICKET_ASSIGNMENT_DEBOUNCE_TIME, { optional: true }),
    questionnaireApiService = inject(QuestionnaireApiService)
  ) =>
    actions$.pipe(
      ofType(AppFeatureActions.ticketAssignmentFeatureAdded),
      concatLatestFrom(() => store.select(selectBookedTariffUuids)),
      map(([_, tariffUuids]) => ({ tariffUuids })),
      getTicketHolderQuestionnairePipe(store, ticketAssignmentDebounceTime, questionnaireApiService),
      map((setTicketHolderFormsAndQuestionnaireDynamicFormFields) =>
        TicketAssignmentActions.setTicketHolderFormsAndQuestionnaireDynamicFieldsOnAddedFeature(
          setTicketHolderFormsAndQuestionnaireDynamicFormFields
        )
      )
    ),
  { functional: true }
);

export const addUnsavedBuyerInformationFormFieldsToTicketHolderOnAddedTicketAssignmentFeature = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AppFeatureActions.ticketAssignmentFeatureAdded),
      concatLatestFrom(() => [
        store.select(selectBuyerPerson),
        store.select(selectBuyerInformationFormFields),
        store.select(selectBuyerQuestionnaireFormFields)
      ]),
      filter(([_, buyerPerson]) => !isEmpty(buyerPerson?.uuid)),
      switchMap(([_, buyerPerson, buyerInformationFormFields, buyerQuestionnaireFormFields]) =>
        store.pipe(
          select(selectTicketAssignmentTicketHolderByPersonUuid(buyerPerson.uuid)),
          first(),
          filter((ticketHolder) => isEmpty(ticketHolder)),
          map(() => ({
            buyerPerson,
            buyerInformationFormFields,
            buyerQuestionnaireFormFields
          })),
          filter(({ buyerPerson }) => isNull(buyerPerson?.id)),
          map(({ buyerPerson, buyerInformationFormFields, buyerQuestionnaireFormFields }) =>
            TicketHolderActions.addTicketHolderAndSetFormFields({
              person: buyerPerson,
              formFields: merge(
                {},
                setDisabledFormFields(buyerPerson, buyerInformationFormFields),
                buyerQuestionnaireFormFields
              )
            })
          )
        )
      )
    ),
  { functional: true }
);

export const addTicketHolderForms$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BookingActions.setBookedTariffUuids),
      map(({ addBookedTariffUuids }) => TicketAssignmentActions.addTicketHolderForms(addBookedTariffUuids))
    ),
  { functional: true }
);

export const removeTicketHolderForms$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BookingActions.removeBookedTariffUuids),
      map(({ bookedTariffUuids }) => TicketAssignmentActions.removeTicketHolderForms(bookedTariffUuids))
    ),
  { functional: true }
);

export const updateTicketHoldersToSavedTicketHolders$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SavedTicketHolderActions.setCreatedSavedTicketHolderPersonsBeforeSaveOrder),
      filter(({ createdTicketHolders }) => !isEmpty(createdTicketHolders)),
      concatLatestFrom(() => store.select(selectSavedTicketHolders)),
      map(([{ createdTicketHolders }, savedTicketHolders]) =>
        TicketHolderActions.updateTicketHoldersToSavedTicketHolders({
          persons: _map(createdTicketHolders, ({ person }) => person),
          removeTicketHolders: !isEmpty(savedTicketHolders)
        })
      )
    ),
  { functional: true }
);

export const updateBuyerTicketHolderToSavedBuyerTicketHolderAfterBuyerFormPrefill$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BuyerFormsSyncActions.setBuyerInformationFormPrefill),
      filter(({ prefillBuyerInformationForm }) => prefillBuyerInformationForm),
      concatLatestFrom(() => store.select(selectBuyerQuestionnaireFormFields)),
      map(([{ person, formFields: savedTicketHolderFormFields }, buyerQuestionnaireFormFields]) =>
        TicketHolderActions.updateBuyerTicketHolderToSavedBuyerTicketHolder({
          person,
          formFields: merge({}, savedTicketHolderFormFields, buyerQuestionnaireFormFields)
        })
      )
    ),
  { functional: true }
);

export const setTicketHolderFields$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        FormFieldSyncActions.setTicketHolderFormFields,
        FormFieldSyncActions.setTicketHolderQuestionnaireFormFields
      ),
      debounceTime(50),
      filter(({ person }) => isNil(person.id)),
      map((setTicketHolderFormFields) => TicketHolderActions.setTicketHolderFields(setTicketHolderFormFields))
    ),
  { functional: true }
);

export const uploadTicketHolderCVDocumentsAndSyncFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), uploadTariffDocumentAPI = inject(UploadTariffDocumentAPI)) =>
    actions$.pipe(
      ofType(TicketHolderDocumentActions.uploadTicketHolderCVDocuments),
      filter(({ fileKind }) => fileKind == FileKind.CV),
      withLatestFrom(store.select(getSelectedExhibitionId), store.select(getOrderUuid)),
      switchMap(([{ isGDPRConsentChecked, ticketPersonId, person, fileKind, files }, eventId, orderUuid]) =>
        uploadTariffDocumentAPI
          .postUploadTariffDocument$(eventId!, ticketPersonId, orderUuid, person.uuid, fileKind, files)
          .pipe(
            map((uploadTariffDocumentResponse) => ({
              isGDPRConsentChecked,
              person,
              uploadedFiles: mapUploadTariffDocumentResponseToUploadedFiles(
                fileKind,
                files,
                uploadTariffDocumentResponse
              )
            })),
            catchError(() => EMPTY)
          )
      ),
      map(({ isGDPRConsentChecked, person, uploadedFiles }) =>
        FormFieldSyncActions.setFormFields(syncCVDocumentFormFields(isGDPRConsentChecked, person, uploadedFiles))
      )
    ),
  { functional: true }
);

export const deleteTicketHolderCVDocumentsAndSyncFormFields$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), uploadTariffDocumentAPI = inject(UploadTariffDocumentAPI)) =>
    actions$.pipe(
      ofType(TicketHolderDocumentActions.deleteUploadedTicketHolderCVDocuments),
      filter(({ fileKind }) => fileKind == FileKind.CV),
      withLatestFrom(store.select(getOrderUuid)),
      mergeMap(([{ isGDPRConsentChecked, person, fileIds }, orderUuid]) =>
        forkJoin(
          _map(fileIds, (fileId) =>
            uploadTariffDocumentAPI.deleteUploadedTariffDocument$(orderUuid, person.uuid, fileId)
          )
        ).pipe(
          map(() => ({ isGDPRConsentChecked, person })),
          catchError(() => EMPTY)
        )
      ),
      map(({ isGDPRConsentChecked, person }) =>
        FormFieldSyncActions.setFormFields(syncGDPRConsentAndCVDocumentFormFields(isGDPRConsentChecked, person, []))
      )
    ),
  { functional: true }
);

export const addTicketHolderFormsAndDynamicQuestionnaireFields$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    ticketAssignmentDebounceTime = inject(TICKET_ASSIGNMENT_DEBOUNCE_TIME, { optional: true }),
    questionnaireApiService = inject(QuestionnaireApiService)
  ) =>
    actions$.pipe(
      ofType(TicketAssignmentActions.addTicketHolderForms),
      getTicketHolderQuestionnairePipe(store, ticketAssignmentDebounceTime, questionnaireApiService),
      map((setTicketHolderFormsAndQuestionnaireDynamicFormFields) =>
        TicketAssignmentActions.addTicketHolderFormsAndQuestionnaireDynamicFields(
          setTicketHolderFormsAndQuestionnaireDynamicFormFields
        )
      )
    ),
  {
    functional: true
  }
);

export const setTicketHolderQuestionnaireFormOnAssignTicketHolder = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TicketHolderActions.assignTicketHolder),
      concatLatestFrom(({ selectedTariffUuid }) =>
        store.select(selectSelectedTicketSendingOptionByTariffUuid(selectedTariffUuid))
      ),
      filter(
        ([_, selectedTicketSendingOption]) => selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink
      ),
      switchMap(([{ selectedTariffUuid, person, formFields }]) =>
        iif(
          () => person?.type === PersonType.BuyerIsHolder,
          store.pipe(
            select(selectTicketPersonIdByBookedTariffUuid(selectedTariffUuid)),
            first(),
            switchMap((ticketPersonId) =>
              store.pipe(select(selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId(ticketPersonId)), first())
            ),
            map((dynamicQuestionnaireFields) =>
              TicketAssignmentActions.addTicketHolderQuestionnaireFormOnTicketHolderAssign(
                person,
                setSyncedQuestionnaireFormFieldsVisibility(dynamicQuestionnaireFields, formFields)
              )
            )
          ),
          of(TicketAssignmentActions.removeTicketHolderQuestionnaireFormOnTicketHolderAssign())
        )
      )
    ),
  { functional: true }
);

export const setTicketHolderQuestionnaireFormOnSendingOptionChange = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TicketSendingOptionsActions.setTicketSendingOptions),
      switchMap(({ addBookedTariffUuids, selectedTicketSendingOption }) =>
        iif(
          () => selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink,
          combineLatest({
            savedTicketHolderForms: store.select(selectSavedTicketHolders),
            ticketHolderBuyerForms: store.select(selectTicketHolderBuyerForms)
          }).pipe(
            first(),
            filter(({ ticketHolderBuyerForms }) => !isEmpty(ticketHolderBuyerForms)),
            map(({ savedTicketHolderForms }) =>
              TicketAssignmentActions.addTicketHolderQuestionnaireFormsOnSendingOptionChange(
                addBookedTariffUuids,
                savedTicketHolderForms
              )
            )
          ),
          store.pipe(
            select(selectTicketHolderQuestionnaireForms),
            first(),
            filter((ticketHolderQuestionnaireForms) => !isEmpty(ticketHolderQuestionnaireForms)),
            map(() =>
              TicketAssignmentActions.removeTicketHolderQuestionnaireFormsOnSendingOptionChange(addBookedTariffUuids)
            )
          )
        )
      )
    ),
  { functional: true }
);
